import { useMemo, useState, useCallback } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Flex,
  Heading,
  IconButton,
  PlusIcon,
  ScrollArea,
  Text,
} from '@buffer-mono/popcorn'

import { actions } from '~publish/legacy/hashtag-group-manager/reducer'
import { selectHashtagGroups } from '~publish/legacy/hashtag-group-manager/selectors'
import type { HashtagGroup } from '~publish/legacy/hashtag-group-manager/types'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'

import { countHashtagsInText } from '../../utils/HashtagCounter'
import { HashtagGroupForm } from '../HashtagGroupForm'
import { HashtagGroupItem } from '../HashtagGroupItem/HashtagGroupItem'
import { HashtagsEmptyIllustration } from '../HashtagGroupManager/HashtagsEmptyIllustration'

import styles from './HashtagGroupManager.module.css'

export interface HashtagGroupManagerProps {
  onInsertHashtag?: (text: string) => void
}

export const HashtagGroupManager = ({
  onInsertHashtag,
}: HashtagGroupManagerProps): JSX.Element => {
  const [mode, setMode] = useState<'manage' | 'create' | 'edit'>('manage')
  const dispatch = useAppDispatch()
  const hashtagGroups = useAppSelector(selectHashtagGroups)

  const onCreateNewHashtagGroup = (): void => {
    setMode('create')
    dispatch(actions.handleChangeGroupName({ name: '' }))
    dispatch(actions.handleChangeGroupText({ text: '' }))
  }

  const onEditHashtagGroup = useCallback(
    (group: HashtagGroup): void => {
      // Store the currently focused element before switching to edit mode
      setMode('edit')
      dispatch(
        actions.handleEditHashtagGroup({
          name: group.name,
          text: group.text,
          snippetId: group._id,
        }),
      )
    },
    [dispatch],
  )

  const onInsertHashtagGroup = (name: string, text: string): void => {
    onInsertHashtag?.(text)
    dispatch(actions.handleInsertHashtagGroupClick({ name, text }))
  }

  const onSaveHashtagGroup = (): void => {
    dispatch(actions.handleSaveHashtagGroupClick())
    setMode('manage')
  }

  const onUpdateHashtagGroup = (): void => {
    dispatch(actions.handleUpdateHashtagGroupClick())
    setMode('manage')
  }

  const onDeleteHashtagGroup = (group: HashtagGroup): void => {
    dispatch(
      actions.handleDeleteHashtagGroup({
        name: group.name,
        text: group.text,
        snippetId: group._id,
      }),
    )
  }

  const onChangeGroupName = (name: string): void => {
    dispatch(
      actions.handleChangeGroupName({
        name,
      }),
    )
  }

  const onChangeGroupText = (text: string): void => {
    dispatch(
      actions.handleChangeGroupText({
        text,
      }),
    )
  }

  const onCancelForm = useCallback((): void => {
    setMode('manage')
    dispatch(actions.handleCancelHashtagGroupClick())
  }, [dispatch])

  const headerCopy = useMemo(() => {
    switch (mode) {
      case 'manage':
        return 'Hashtag Groups'
      case 'create':
        return 'Create Hashtag Group'
      case 'edit':
        return 'Edit Hashtag Group'
    }
  }, [mode])

  if (mode === 'manage' && (!hashtagGroups || !hashtagGroups.length)) {
    return (
      <Flex
        direction="column"
        align="center"
        gap="lg"
        className={styles.emptyState}
      >
        <HashtagsEmptyIllustration />
        <Flex direction="column" gap="xs" align="center">
          <Text as="span" weight="medium">
            No hashtag groups yet!
          </Text>
          <Text as="span" align="center">
            Save and reuse your favorite hashtags by creating a hashtag group.
          </Text>
        </Flex>
        <Button onClick={onCreateNewHashtagGroup}>
          <PlusIcon /> Create Hashtag Group
        </Button>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="sm" fullHeight>
      <Flex
        align="center"
        justify="between"
        fullWidth
        className={styles.header}
      >
        <Heading as="h4">{headerCopy}</Heading>
        {mode === 'manage' && (
          <IconButton
            label="Create New Hashtag Group"
            variant="secondary"
            size="small"
            onClick={onCreateNewHashtagGroup}
          >
            <PlusIcon />
          </IconButton>
        )}
      </Flex>

      {mode === 'create' && (
        <HashtagGroupForm
          onChangeGroupName={onChangeGroupName}
          onChangeGroupText={onChangeGroupText}
          onCancel={onCancelForm}
          onSubmit={onSaveHashtagGroup}
        />
      )}
      {mode === 'edit' && (
        <HashtagGroupForm
          onChangeGroupName={onChangeGroupName}
          onChangeGroupText={onChangeGroupText}
          onCancel={onCancelForm}
          onSubmit={onUpdateHashtagGroup}
        />
      )}
      {mode === 'manage' && (
        <ScrollArea visibility="hover">
          <Flex direction="column" gap="xs" fullWidth className={styles.list}>
            {hashtagGroups.map((group) => (
              <HashtagGroupItem
                key={group.id || group._id}
                group={group}
                hashtagsCount={countHashtagsInText(group.text)}
                onInsert={(): void => {
                  onInsertHashtagGroup(group.name, group.text)
                }}
                onEdit={onEditHashtagGroup}
                onDelete={onDeleteHashtagGroup}
              />
            ))}
          </Flex>
        </ScrollArea>
      )}
    </Flex>
  )
}
